import React, { StrictMode, Suspense } from 'react';
import {
  isMultiColorActive,
  defaultColor,
  isDarkSwitchActive,
} from '../src/constants/defaultValues';
import { getCurrentColor, setCurrentColor, getCurrentUser, getAccessToken } from '../src/helpers/Utils';
import 'bootstrap/dist/css/bootstrap.min.css';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from './redux/reducers';
import { NotificationProvider } from '@/views/app/ui/components/notification/notification-context';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { buildMsalConfig, getTenantCode } from '@/helpers/auth-configration';
import { isAzureAuth } from '@/constants/defaultValues';
import axios from 'axios';
import { loadApiConfig, getApiBaseUrl } from '@/constants/apiConfig';
import { COMPANY_GET_TENANT_CONFIG } from '@/redux/RTK/OragnizationsQuerys/Endpoints';
import LangProvider from '@/context/LangProvider';
import ErrorBoundary from '@/helpers/error-boundary';
import EnhancedErrorBoundary from '@/components/common/EnhancedErrorBoundary';
import errorLogger from '@/helpers/errorLogger';
import './assets/css/main.css'
import ApiResponseModalProvider from '@/config/Provider/ApiResponseModalProvider';
import { MsalProvider } from "@azure/msal-react";
const App = React.lazy(() => import(/* webpackChunkName: "app" */ './App'));

const color =
  isMultiColorActive || isDarkSwitchActive ? getCurrentColor() : defaultColor;
setCurrentColor(color);

// Apply theme class to <body> early (before React mounts) to prevent flash of wrong theme
try {
  const preferredTheme = localStorage.getItem('PreferredTheme');
  const themeColor = preferredTheme || color;
  if (themeColor) {
    const themeClass = `theme-${String(themeColor).toLowerCase()}`;
    // Remove any existing theme-* class first
    document.body.classList.forEach((cls) => {
      if (cls.startsWith('theme-')) document.body.classList.remove(cls);
    });
    document.body.classList.add(themeClass);
  }
} catch (e) {}

// Loading fallback component
const LoadingFallback = () => (
  <div className="ta-loader">
    <div className="loader">
      <span className="loader-text"></span>
      <span className="load"></span>
    </div>
  </div>
);

// export const msalInstance = new PublicClientApplication(msalConfig);
// msalInstance.initialize().then(() => {
//   if (
//     !msalInstance.getActiveAccount() &&
//     msalInstance.getAllAccounts().length > 0
//   ) {
//     console.log(msalInstance.getAllAccounts()[0]);
//     // Account selection logic is app dependent. Adjust as needed for different use cases.
//     msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
//   }

//   // Optional - This will update account state if a user signs in from another tab or window
//   msalInstance.enableAccountStorageEvents();

//   msalInstance.addEventCallback((event) => {
//     if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
//       console.log(event.payload.account);
//       const account = event.payload.account;
//       msalInstance.setActiveAccount(account);
//     }
//   });
// });

const root = ReactDOM.createRoot(document.getElementById('root'));
export let msalInstance = null;

 

const getAzureTenantCodeFromUrl = () => {
  const hashPath = String(window.location.hash || '').replace(/^#\/?/, '');
  const [tenantCode, page] = hashPath.split('/');
  if (!tenantCode || !['azure-login', 'login'].includes(page)) return '';
  return decodeURIComponent(tenantCode);
};

const prefetchAzureTenantConfig = async () => {
  const tenantCode = getAzureTenantCodeFromUrl();
  if (!tenantCode) return;

  const rawConfig = localStorage.getItem('azureTenantConfig');
  if (rawConfig) {
    try {
      const parsed = JSON.parse(rawConfig);
      const parsedTenantCode = parsed?.tenantCode || parsed?.code;
      if (parsed?.clientID && parsed?.tenantID && parsedTenantCode === tenantCode) {
        localStorage.removeItem('azureTenantConfigError');
        return;
      }
    } catch {}
  }

  localStorage.removeItem('azureTenantConfig');
  localStorage.removeItem('azureTenantConfigError');
  localStorage.setItem('tenantCode', tenantCode);

  try {
    await loadApiConfig();
    const response = await axios.get(
      `${getApiBaseUrl()}${COMPANY_GET_TENANT_CONFIG}`,
      {
        params: { code: tenantCode },
        headers: { 'X-Tenant-Code': tenantCode },
      }
    );
    const config = response?.data?.data ?? response?.data;
    if (config?.clientID && config?.tenantID) {
      localStorage.setItem('azureTenantConfig', JSON.stringify({ ...config, tenantCode }));
      return;
    }
    localStorage.setItem('azureTenantConfigError', JSON.stringify(response?.data || {}));
  } catch (error) {
    localStorage.setItem('azureTenantConfigError', JSON.stringify(error?.response?.data || error?.message || {}));
  }
};

const resolveLoginRouteFromTenantConfig = () => {
  const tenantPrefix = getTenantCode() ? `/${encodeURIComponent(getTenantCode())}` : '';
  try {
    const raw = localStorage.getItem('azureTenantConfig');
    if (!raw) return isAzureAuth === 1 ? `${tenantPrefix}/azure-login` : '/ad-normal-login';
    const parsed = JSON.parse(raw);
    const loginType = parsed?.loginType ?? parsed?.data?.loginType;
    if (Number(loginType) === 0) return `${tenantPrefix}/normal-login`;
    if (Number(loginType) === 1) return `${tenantPrefix}/azure-login`;
    if (Number(loginType) === 3) {
      const loginCriteria = localStorage.getItem('LoginCriteria');
      if (loginCriteria === 'normal') return `${tenantPrefix}/normal-login`;
      if (loginCriteria === 'azure') return `${tenantPrefix}/azure-login`;
      return `${tenantPrefix}/normal-login`;
    }
    return isAzureAuth === 1 ? `${tenantPrefix}/azure-login` : '/ad-normal-login';
  } catch {
    return isAzureAuth === 1 ? `${tenantPrefix}/azure-login` : '/ad-normal-login';
  }
};

// Build marker: Azure login flow fix (azureLoginNavScheduled + employeeDetails fallback)
// Update the version string when making breaking changes to the login/redirect flow.
const TA_AZURE_LOGIN_FIX_VERSION = '2026-09-30-azure-fix-v1';

const renderApp = () => {
  try {
    if (typeof window !== 'undefined') {
      window.__TA_AZURE_LOGIN_FIX_VERSION__ = TA_AZURE_LOGIN_FIX_VERSION;
      // This log is used to verify in production which build is running.
      console.log('TA Azure Login Fix Version:', TA_AZURE_LOGIN_FIX_VERSION);
    }
  } catch (e) {}

  root.render(
    <StrictMode>
      <EnhancedErrorBoundary>
        <Provider store={store}>
          <ApiResponseModalProvider />
          <NotificationProvider>
            <LangProvider>
              <ErrorBoundary>
                <MsalProvider instance={msalInstance}>
                <Suspense fallback={null}>
                  <App />
                </Suspense>
                </MsalProvider>
              </ErrorBoundary>
            </LangProvider>
          </NotificationProvider>
        </Provider>
      </EnhancedErrorBoundary>
    </StrictMode>
  );
};

const bootstrapApp = async () => {
  await prefetchAzureTenantConfig();
  const resolvedMsalConfig = buildMsalConfig();
  console.log('MSAL config built:', resolvedMsalConfig?.auth?.clientId, resolvedMsalConfig?.auth?.authority, resolvedMsalConfig?.auth?.redirectUri);

  msalInstance = new PublicClientApplication(resolvedMsalConfig);

  await msalInstance.initialize();

  // Handle redirect response if returning from Microsoft login
  let azureLoginNavScheduled = false;
  try {
    console.log('Calling handleRedirectPromise...');
    const response = await msalInstance.handleRedirectPromise();
    console.log('handleRedirectPromise completed, response:', response);
    if (response) {
      console.log('MSAL redirect response - account:', response.account?.username);
      msalInstance.setActiveAccount(response.account);

      // We keep MSAL redirectUri as a non-hash URL (e.g. http://localhost:3000) to match Azure App Registration.
      // But the app uses HashRouter, and AzureLogin route is responsible for backend token hydration.
      // Force navigation into the hash route after successful redirect handling.
      if (isAzureAuth === 1) {
        const tenantPrefix = getTenantCode() ? `/${encodeURIComponent(getTenantCode())}` : '';
        const expectedHash = `#${tenantPrefix}/azure-login`;
        const currentHash = window.location.hash || '';
        if (!currentHash.startsWith(expectedHash)) {
          const target = `${window.location.origin}${window.location.pathname}${expectedHash}`;
          console.log('Index: Forcing navigation to AzureLogin hash route:', target);
          azureLoginNavScheduled = true;
          // Do not return early; ensure the app mounts even if navigation is delayed/blocked.
          setTimeout(() => {
            try {
              window.location.replace(target);
            } catch (e) {
              console.error('Index: Failed to force navigation to AzureLogin:', e);
            }
          }, 0);
        }
      }
    }
  } catch (error) {
    console.error('MSAL handleRedirectPromise error:', error);
  }

  const accounts = msalInstance.getAllAccounts();
  console.log('MSAL accounts after redirect handling:', accounts.length);

  if (!msalInstance.getActiveAccount() && accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.enableAccountStorageEvents();

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      console.log('MSAL LOGIN_SUCCESS event:', event.payload.account);
      msalInstance.setActiveAccount(event.payload.account);
    }
  });

  try {
    const user = getCurrentUser();
    const token = getAccessToken();
    const hasId = user?.id !== undefined && user?.id !== null;
    const hasToken = !!token;
    const searchHasCode = /(^|[?&#])code=/.test(window.location.search || '');
    const h = (window.location.hash || '').toLowerCase();
    const onPublic = h.includes('login') || h.includes('azure-login') || h.includes('normal-login') || h.includes('ad-normal-login') || h.includes('logout');
    // Skip the /login kick when we just scheduled the azure-login navigation above:
    // it must win so AzureLogin can finish the backend token exchange after a
    // Microsoft redirect (which returns with no backend tokens saved yet).
    if (!(hasId && hasToken) && !searchHasCode && !azureLoginNavScheduled && !onPublic) {
      const target = `${window.location.origin}${window.location.pathname}#/login`;
      setTimeout(() => {
        try { window.location.replace(target); } catch (e) { console.error('Redirect to login failed:', e); }
      }, 0);
    }
  } catch (e) {
    console.error('Unauthorized check failed:', e);
  }

  renderApp();
};

bootstrapApp();

// Register service worker in production
try {
  const canUseSw =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    process.env.NODE_ENV === 'production' &&
    'serviceWorker' in navigator &&
    window.isSecureContext === true;

  if (canUseSw) {
    window.addEventListener('load', () => {
      try {
        navigator.serviceWorker
          .register('/service-worker.js')
          .then((registration) => {
            console.log('SW registered:', registration);
          })
          .catch((error) => {
            console.log('SW registration failed:', error);
          });
      } catch (e) {
        console.warn('SW disabled in this context:', e);
      }
    });
  }
} catch (e) {
}
